import React, { createContext, useContext, useState, useCallback } from 'react';
import type {
    UserTenantResponse,
    UserTenantDetailsResponse,
} from '../api/generated/models';

interface TenantContextValue {
    selectedTenant: UserTenantResponse | null;
    selectTenant: (tenant: UserTenantResponse) => void;
    clearTenant: () => void;
}

const TenantContext = createContext<TenantContextValue | undefined>(undefined);

interface TenantProviderProps {
    children: React.ReactNode;
    initialTenants?: UserTenantDetailsResponse[];
}

export const TenantProvider: React.FC<TenantProviderProps> = ({
    children,
    initialTenants,
}) => {
    const [selectedTenant, setSelectedTenant] =
        useState<UserTenantResponse | null>(null);

    const selectTenant = useCallback((tenant: UserTenantResponse) => {
        setSelectedTenant(tenant);
    }, []);

    const clearTenant = useCallback(() => {
        setSelectedTenant(null);
    }, []);

    return (
        <TenantContext.Provider
            value={{ selectedTenant, selectTenant, clearTenant }}
        >
            {children}
        </TenantContext.Provider>
    );
};

export const useTenant = (): TenantContextValue => {
    const context = useContext(TenantContext);
    if (!context) {
        return {
            selectedTenant: null,
            selectTenant: () => undefined,
            clearTenant: () => undefined,
        };
    }
    return context;
};

export default TenantContext;
